import { t } from '../i18n';

type EmptyVariant = 'no-results' | 'no-access' | 'fetch-failed';

interface EmptyStateProps {
  variant: EmptyVariant;
  errorCode?: string;
  onAction?: () => void;
}

const ICONS: Record<EmptyVariant, string> = {
  'no-results': '◎',
  'no-access': '◈',
  'fetch-failed': '⚠',
};

export default function EmptyState({ variant, errorCode, onAction }: EmptyStateProps) {
  const isError = variant === 'fetch-failed';

  const eyebrow =
    variant === 'no-results'
      ? t('marketplace.empty.noResults.eyebrow')
      : variant === 'no-access'
      ? t('marketplace.empty.noAccess.eyebrow')
      : t('marketplace.empty.fetchFailed.eyebrow');

  const title =
    variant === 'no-results'
      ? t('marketplace.empty.noResults.title')
      : variant === 'no-access'
      ? t('marketplace.empty.noAccess.title')
      : t('marketplace.empty.fetchFailed.title');

  const body =
    variant === 'no-results'
      ? t('marketplace.empty.noResults.body')
      : variant === 'no-access'
      ? t('marketplace.empty.noAccess.body')
      : t('marketplace.empty.fetchFailed.body');

  const action =
    variant === 'no-results'
      ? t('marketplace.empty.noResults.action')
      : variant === 'no-access'
      ? t('marketplace.empty.noAccess.action')
      : t('marketplace.empty.fetchFailed.action');

  return (
    <div
      style={{
        background: 'var(--bg-canvas)',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'var(--border-subtle)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--sp-12) var(--sp-8)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 'var(--sp-4)',
        textAlign: 'center',
        maxWidth: '480px',
        margin: '0 auto',
      }}
    >
      {/* Icon circle */}
      <div
        style={{
          width: '44px',
          height: '44px',
          borderRadius: '50%',
          background: isError ? 'var(--dp-warn-100)' : 'var(--dp-canvas-200)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '20px',
          color: 'var(--dp-navy-800)',
        }}
        aria-hidden="true"
      >
        {ICONS[variant]}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
        <p className="dp-eyebrow">{eyebrow}</p>
        <h3 style={{ fontFamily: 'var(--font-serif)', fontSize: 'var(--fs-h3)', fontWeight: 600 }}>
          {title}
        </h3>
        <p style={{ fontSize: 'var(--fs-body-sm)', color: 'var(--fg-3)', margin: 0 }}>{body}</p>
        {errorCode && (
          <p style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-meta)', color: 'var(--fg-4)', margin: 0 }}>
            {errorCode}
          </p>
        )}
      </div>

      <button className="dp-btn dp-btn--ghost" onClick={onAction}>
        {action}
      </button>
    </div>
  );
}
